'use client';

import { FC, useState, useEffect, useRef } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import Link from 'next/link';
import { registerContact } from '@/actions/hubspot/registerContact';
import cookies from 'js-cookie';
import { useIsClient } from '@/hooks';

// HubSpot form configuration
const DEFAULT_HUBSPOT_PORTAL_ID = '7119515';
const DEFAULT_HUBSPOT_FORM_ID = 'c54880e9-1a1d-4609-a81e-a1133f6b5d1c';

// Field label to HubSpot internal name mapping.
// Keys are lowercased + trimmed labels (asterisks/colons stripped); values are
// the exact internal property names that HubSpot expects on form submission.
const FIELD_MAPPINGS: Record<string, string> = {
  // First name variations
  name: 'firstname',
  'first name': 'firstname',
  firstname: 'firstname',
  'full name': 'firstname',
  'your name': 'firstname',
  // Last name variations
  'last name': 'lastname',
  lastname: 'lastname',
  surname: 'lastname',
  // Email variations
  email: 'email',
  'email address': 'email',
  'your email': 'email',
  'work email': 'email',
  // Company variations
  company: 'company',
  'company name': 'company',
  'your company': 'company',
  organization: 'company',
  // Job title variations
  'job title': 'jobtitle',
  jobtitle: 'jobtitle',
  title: 'jobtitle',
  role: 'jobtitle',
  position: 'jobtitle',
  // Phone variations
  phone: 'phone',
  'phone number': 'phone',
  mobile: 'phone',
};

/**
 * Props for `LeadMagnetWithForm`.
 */
export type LeadMagnetWithFormProps =
  SliceComponentProps<Content.LeadMagnetWithFormSlice>;

/**
 * Component for "LeadMagnetWithForm" Slices.
 * Lead magnet section with resource image on one side and capture form on the other.
 */
const LeadMagnetWithForm: FC<LeadMagnetWithFormProps> = ({ slice }) => {
  const isNoSideImage = slice.variation === 'noSideImage';
  const isFormDescAndGreenButton = slice.variation === 'formDescAndGreenButton';
  const isOnGlass = slice.variation === 'onGlass';
  const isBlackHeaderAndCallout = slice.variation === 'blackHeaderAndCallout';
  const isModernTitleCenteredForm =
    slice.variation === 'modernTitleCenteredForm';

  const { logo, title, description, form_fields, button_text } = slice.primary;

  // Extract resource_image if it exists
  const resource_image =
    'resource_image' in slice.primary ? slice.primary.resource_image : null;

  // Extract form_title for noSideImage variation
  const form_title =
    'form_title' in slice.primary ? (slice.primary.form_title as string) : null;

  // Extract form_description for formDescAndGreenButton and modernTitleCenteredForm variations
  const form_description =
    'form_description' in slice.primary ? slice.primary.form_description : null;

  // Extract background from slice.primary
  const background =
    'background' in slice.primary ? slice.primary.background : null;

  // Extract illustration for blackHeaderAndCallout variation
  const illustration =
    'illustration' in slice.primary ? slice.primary.illustration : null;

  // Extract green_headline for modernTitleCenteredForm variation
  const green_headline =
    'green_headline' in slice.primary
      ? (slice.primary.green_headline as string)
      : null;

  // Extract sub_header for modernTitleCenteredForm variation
  const sub_header =
    'sub_header' in slice.primary ? slice.primary.sub_header : null;

  // Use form_id from Prismic if provided, otherwise use default
  const formId =
    'form_id' in slice.primary ? (slice.primary.form_id as string) : null;
  const hubspotFormId = formId || DEFAULT_HUBSPOT_FORM_ID;

  const logoRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const formAreaRef = useRef<HTMLDivElement>(null);

  const isClient = useIsClient();
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isClient) {
      return;
    }

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    const elementsToAnimate = [
      { ref: logoRef, delay: '0ms' },
      { ref: contentRef, delay: '150ms' },
      { ref: formAreaRef, delay: '300ms' },
    ];

    elementsToAnimate.forEach(({ ref, delay }) => {
      if (ref.current) {
        ref.current.style.transitionDelay = delay;
        observer.observe(ref.current);
      }
    });

    return () => {
      elementsToAnimate.forEach(({ ref }) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, [isClient]);

  const handleInputChange = (fieldLabel: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [fieldLabel]: value,
    }));
    if (fieldErrors[fieldLabel]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldLabel];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);
    setFieldErrors({});

    // Validate required fields
    const newErrors: Record<string, string> = {};
    form_fields?.forEach((field) => {
      if (field.required && !formData[field.field_label || '']?.trim()) {
        newErrors[field.field_label || ''] = 'This field is required';
      }
      // Validate email format
      if (
        field.field_type === 'email' &&
        formData[field.field_label || '']?.trim()
      ) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData[field.field_label || ''])) {
          newErrors[field.field_label || ''] = 'Please enter a valid email';
        }
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      // Convert form field labels to HubSpot field names
      const hubspotProperties: Record<string, string> = {};

      Object.entries(formData).forEach(([label, value]) => {
        // Strip required-asterisks, colons, etc. so labels like "Job title *"
        // or "Email:" still find their mapping.
        const normalizedLabel = label
          .toLowerCase()
          .replace(/[*:]/g, '')
          .replace(/\s+/g, ' ')
          .trim();
        const fieldName =
          FIELD_MAPPINGS[normalizedLabel] ||
          normalizedLabel.replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        hubspotProperties[fieldName] = value;
      });

      const { postSubmitAction } = await registerContact({
        properties: hubspotProperties,
        pageUri: typeof window !== 'undefined' ? window.location.href : '',
        pageName: typeof document !== 'undefined' ? document.title : '',
        hubspotPortalIdForm: DEFAULT_HUBSPOT_PORTAL_ID,
        hubspotGuidForm: hubspotFormId,
        hubspotutkCookie: cookies.get('hubspotutk'),
      });

      if (postSubmitAction?.type === 'redirect' && postSubmitAction.value) {
        window.location.href = postSubmitAction.value;
        return;
      }

      // Build redirect URL with UTM parameters
      const currentUrl = new URL(window.location.href);
      const redirectParams = new URLSearchParams();

      // Preserve existing UTM parameters
      const utmParams = [
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_term',
        'utm_content',
      ];
      utmParams.forEach((param) => {
        const value = currentUrl.searchParams.get(param);
        if (value) {
          redirectParams.set(param, value);
        }
      });

      // Add download identification
      let headlineText = '';
      if (title && title.length > 0) {
        const firstNode = title[0];
        if (firstNode && 'text' in firstNode) {
          headlineText = (firstNode as { text: string }).text;
        }
      }
      const downloadName = headlineText || document.title || 'ebook-download';
      redirectParams.set(
        'download',
        downloadName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, ''),
      );

      // Add source page
      redirectParams.set('source_page', currentUrl.pathname);

      // Redirect to thank you page
      const thankYouUrl = `/thank-you-for-your-download?${redirectParams.toString()}`;
      window.location.href = thankYouUrl;
    } catch (error) {
      console.error('Form submission error:', error);
      setErrorMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFormField = (
    field:
      | Content.LeadMagnetWithFormSliceLeadMagnetWithSideImageFormPrimaryFormFieldsItem
      | Content.LeadMagnetWithFormSliceNoSideImagePrimaryFormFieldsItem
      | Content.LeadMagnetWithFormSliceOnGlassPrimaryFormFieldsItem
      | Content.LeadMagnetWithFormSliceBlackHeaderAndCalloutPrimaryFormFieldsItem
      | Content.LeadMagnetWithFormSliceModernTitleCenteredFormPrimaryFormFieldsItem
      | Content.LeadMagnetWithFormSliceFormDescAndGreenButtonPrimaryFormFieldsItem,
    index: number,
    inputBgColor?: string,
  ) => {
    const fieldLabel = field.field_label || '';
    const isRequired = field.required;
    const fieldType = field.field_type || 'text';
    const fieldId = fieldLabel.toLowerCase().replace(/\s+/g, '_');
    const bgColor = inputBgColor || 'rgba(67,68,68,0.4)';

    return (
      <div key={index} className="flex flex-col gap-2 w-full">
        <label
          htmlFor={fieldId}
          className="text-base text-white font-normal leading-[1.5]"
        >
          {fieldLabel}
          {isRequired && ' *'}
        </label>
        <input
          id={fieldId}
          type={fieldType === 'email' ? 'email' : 'text'}
          value={formData[fieldLabel] || ''}
          onChange={(e) => handleInputChange(fieldLabel, e.target.value)}
          placeholder="Type here"
          className="w-full h-12 px-3 py-2 rounded-lg text-white placeholder:text-white/30 text-base leading-[1.5] focus:outline-none focus:ring-1 focus:ring-white/30"
          style={{ backgroundColor: bgColor }}
        />
        {fieldErrors[fieldLabel] && (
          <span className="text-red-400 text-sm">
            {fieldErrors[fieldLabel]}
          </span>
        )}
      </div>
    );
  };

  // Render blackHeaderAndCallout variation
  if (isBlackHeaderAndCallout) {
    return (
      <>
        {/* Logo Header Section - Black background */}
        <section className="bg-black">
          <div
            ref={logoRef}
            className="py-[30px] px-5 md:px-16 max-w-[1040px] mx-auto flex justify-center opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            {isFilled.image(logo) && (
              <Link href="/" aria-label="Devsu home page">
                <PrismicNextImage
                  field={logo}
                  className="h-7 md:h-[29px] w-auto object-contain"
                />
              </Link>
            )}
          </div>
        </section>

        {/* Main Content Section */}
        <section
          data-slice-type={slice.slice_type}
          data-slice-variation={slice.variation}
          className="relative px-5 md:px-16"
        >
          {/* Background Image Layer */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Base dark background */}
            <div className="absolute inset-0 bg-[#1c2228]" />

            {/* Background image from Prismic */}
            {isFilled.image(background) && (
              <div className="absolute inset-0 overflow-hidden">
                <PrismicNextImage
                  field={background}
                  className="absolute w-full h-full left-0 top-0 object-cover max-w-none opacity-30"
                  fill={false}
                  priority
                />
              </div>
            )}
          </div>

          <div className="relative pt-[94px] pb-0 max-w-[1040px] mx-auto">
            {/* Content Row */}
            <div
              ref={contentRef}
              className="flex flex-col lg:flex-row gap-10 items-start w-full opacity-0 translate-y-8 transition-all duration-700 ease-out"
            >
              {/* Left Content - Title & Description */}
              <div className="flex-1 flex flex-col gap-10 text-white">
                {isFilled.richText(title) && (
                  <PrismicRichText
                    field={title}
                    components={{
                      heading2: ({ children }) => (
                        <h2 className="text-[32px] md:text-[52px] font-extrabold leading-[1.2]">
                          {children}
                        </h2>
                      ),
                    }}
                  />
                )}

                {isFilled.richText(description) && (
                  <div className="text-lg leading-[1.4]">
                    <PrismicRichText
                      field={description}
                      components={{
                        paragraph: ({ children }) => (
                          <p className="mb-4 last:mb-0">{children}</p>
                        ),
                        em: ({ children }) => (
                          <span className="font-semibold italic">
                            {children}
                          </span>
                        ),
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Right Content - Form Card */}
              <div
                id="lead-magnet-form"
                ref={formAreaRef}
                className="flex-1 py-[30px] opacity-0 translate-y-8 transition-all duration-700 ease-out delay-150 scroll-mt-24"
              >
                <div className="backdrop-blur-[47.5px] rounded-2xl p-6 shadow-[0px_4.325px_64.871px_0px_rgba(0,0,0,0.08)] opacity-90 bg-black max-w-[482px] ml-auto">
                  {form_title && (
                    <p className="text-xl font-medium leading-[1.2] text-white mb-6">
                      {form_title}
                    </p>
                  )}
                  <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                    {form_fields?.map((field, index) =>
                      renderFormField(field, index, '#1c2228'),
                    )}

                    {errorMessage && (
                      <div className="text-red-400 text-sm text-center">
                        {errorMessage}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="primary-button w-full bg-[#05f273] text-black hover:bg-[#05f273]/90 transition-colors duration-200"
                    >
                      {isSubmitting
                        ? 'Submitting...'
                        : button_text || 'Access the Whitepaper'}
                    </button>
                  </form>
                </div>
              </div>
            </div>

            {/* Ebook Illustration */}
            {isFilled.image(illustration) && (
              <div className="relative w-full mt-10">
                <PrismicNextImage
                  field={illustration}
                  className="w-full h-auto object-contain"
                />
              </div>
            )}
          </div>
        </section>
      </>
    );
  }

  // Render modernTitleCenteredForm variation
  if (isModernTitleCenteredForm) {
    return (
      <>
        {/* Logo Header Section */}
        <section className="bg-black">
          <div
            ref={logoRef}
            className="py-[30px] px-5 md:px-16 max-w-[1040px] mx-auto flex justify-center opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            {isFilled.image(logo) && (
              <Link href="/" aria-label="Devsu home page">
                <PrismicNextImage
                  field={logo}
                  className="h-7 md:h-[29px] w-auto object-contain"
                />
              </Link>
            )}
          </div>
        </section>

        {/* Main Content Section */}
        <section
          data-slice-type={slice.slice_type}
          data-slice-variation={slice.variation}
          className="bg-black px-5 md:px-16 py-[50px]"
        >
          <div className="max-w-[940px] mx-auto flex flex-col gap-9 items-center">
            {/* Header Content - Title, Green Headline, Description */}
            <div
              ref={contentRef}
              className="flex flex-col gap-9 items-center text-center w-full opacity-0 translate-y-8 transition-all duration-700 ease-out"
            >
              {/* Titles Section */}
              <div className="flex flex-col gap-6 items-center w-full">
                {/* Main Title - Gray gradient text */}
                {isFilled.richText(title) && (
                  <PrismicRichText
                    field={title}
                    components={{
                      heading2: ({ children }) => (
                        <h2
                          className="text-[40px] md:text-[80px] font-medium leading-none"
                          style={{
                            background:
                              'radial-gradient(ellipse at center, #9a9a9a 0%, #808080 25%, #666666 50%, #4c4c4c 75%, #323232 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                          }}
                        >
                          {children}
                        </h2>
                      ),
                    }}
                  />
                )}

                {/* Green Headline */}
                {green_headline && (
                  <p className="text-[32px] md:text-[48px] font-medium leading-none text-[#05f273] w-full">
                    {green_headline}
                  </p>
                )}

                {/* Sub Header */}
                {isFilled.richText(sub_header) && (
                  <div className="text-lg font-semibold leading-[1.4] text-[#aaaaaa]">
                    <PrismicRichText
                      field={sub_header}
                      components={{
                        paragraph: ({ children }) => (
                          <p className="mb-4 last:mb-0">{children}</p>
                        ),
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Description */}
              {isFilled.richText(description) && (
                <div className="text-lg font-light leading-[1.4] text-[#aaaaaa]">
                  <PrismicRichText
                    field={description}
                    components={{
                      paragraph: ({ children }) => (
                        <p className="mb-4 last:mb-0">{children}</p>
                      ),
                    }}
                  />
                </div>
              )}
            </div>

            {/* Form Card */}
            <div
              id="lead-magnet-form"
              ref={formAreaRef}
              className="backdrop-blur-[47.5px] bg-[#1c2228] rounded-2xl p-6 shadow-[0px_4.325px_64.871px_0px_rgba(0,0,0,0.08)] w-full max-w-[535px] opacity-0 translate-y-8 transition-all duration-700 ease-out delay-150 scroll-mt-24"
            >
              {form_title && (
                <>
                  <p className="text-xl font-medium leading-[1.2] text-white text-center w-full mb-4">
                    {form_title}
                  </p>
                  {form_description && isFilled.richText(form_description) && (
                    <div className="text-base leading-[1.6] text-[#d2d2d2] text-center mb-6">
                      <PrismicRichText
                        field={form_description}
                        components={{
                          paragraph: ({ children }) => (
                            <p className="mb-3 last:mb-0">{children}</p>
                          ),
                        }}
                      />
                    </div>
                  )}
                </>
              )}
              <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                {form_fields?.map((field, index) =>
                  renderFormField(field, index, '#000000'),
                )}

                {errorMessage && (
                  <div className="text-red-400 text-sm text-center">
                    {errorMessage}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="primary-button"
                >
                  {isSubmitting ? 'Submitting...' : button_text || 'Download'}
                </button>
              </form>
            </div>
          </div>
        </section>
      </>
    );
  }

  // Render onGlass variation
  if (isOnGlass) {
    return (
      <>
        {/* Logo Header Section */}
        <section className="bg-[#002726]">
          <div
            ref={logoRef}
            className="py-[30px] px-5 md:px-16 max-w-[1040px] mx-auto flex justify-center opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            {isFilled.image(logo) && (
              <Link href="/" aria-label="Devsu home page">
                <PrismicNextImage
                  field={logo}
                  className="h-7 md:h-[29px] w-auto object-contain"
                />
              </Link>
            )}
          </div>
        </section>

        {/* Main Content Section - Glass card on textured background */}
        <section
          data-slice-type={slice.slice_type}
          data-slice-variation={slice.variation}
          className="relative px-5 md:px-16"
        >
          {/* Background Image Layer */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Base dark background */}
            <div className="absolute inset-0 bg-[#0e1010]" />

            {/* Background image from Prismic */}
            {isFilled.image(background) && (
              <div className="absolute inset-0 overflow-hidden">
                <PrismicNextImage
                  field={background}
                  className="absolute w-full h-[172%] left-0 top-[-50%] object-cover max-w-none"
                  fill={false}
                  priority
                />
              </div>
            )}
          </div>

          <div className="relative py-[80px] max-w-[1040px] mx-auto">
            {/* Glass Card Container */}
            <div
              ref={contentRef}
              className="backdrop-blur-[5px] bg-[rgba(4,17,24,0.7)] rounded-2xl p-6 shadow-[0px_4.325px_64.871px_0px_rgba(0,0,0,0.08)] opacity-0 translate-y-8 transition-all duration-700 ease-out"
            >
              <div className="flex flex-col lg:flex-row gap-10 lg:gap-[60px] items-center">
                {/* Left Content - Title & Description */}
                <div className="flex-1 flex flex-col gap-10 text-white py-6 lg:py-[50px]">
                  {isFilled.richText(title) && (
                    <PrismicRichText
                      field={title}
                      components={{
                        heading2: ({ children }) => (
                          <h2 className="text-[32px] md:text-[52px] font-extrabold leading-[1.2]">
                            {children}
                          </h2>
                        ),
                      }}
                    />
                  )}

                  {isFilled.richText(description) && (
                    <div className="text-lg leading-[1.4]">
                      <PrismicRichText
                        field={description}
                        components={{
                          paragraph: ({ children }) => (
                            <p className="mb-[10px] last:mb-0">{children}</p>
                          ),
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Right Content - Form Card */}
                <div
                  id="lead-magnet-form"
                  ref={formAreaRef}
                  className="w-full lg:flex-1 bg-[#002726] rounded-2xl p-6 flex flex-col justify-between min-h-[400px] opacity-0 translate-y-8 transition-all duration-700 ease-out delay-150 scroll-mt-24"
                >
                  {form_title && (
                    <p className="text-xl font-medium leading-[1.2] text-white mb-6">
                      {form_title}
                    </p>
                  )}
                  <form
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-6 flex-1 justify-between"
                  >
                    <div className="flex flex-col gap-4">
                      {form_fields?.map((field, index) =>
                        renderFormField(field, index),
                      )}
                    </div>

                    {errorMessage && (
                      <div className="text-red-400 text-sm text-center">
                        {errorMessage}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[rgba(234,234,234,0.3)] border-t border-[#eaeaea] rounded-full px-6 py-3 font-bold text-white text-center hover:bg-white/40 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting
                        ? 'Submitting...'
                        : button_text || 'Access the Ebook'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  // Render noSideImage and formDescAndGreenButton variations
  if (isNoSideImage || isFormDescAndGreenButton) {
    return (
      <>
        {/* Logo Header Section */}
        <section className="bg-[#002726]">
          <div
            ref={logoRef}
            className="py-[30px] px-4 md:px-[200px] flex justify-center opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            {isFilled.image(logo) && (
              <Link href="/" aria-label="Devsu home page">
                <PrismicNextImage
                  field={logo}
                  className="h-7 md:h-[29px] w-auto object-contain"
                />
              </Link>
            )}
          </div>
        </section>

        {/* Main Content Section */}
        <section
          data-slice-type={slice.slice_type}
          data-slice-variation={slice.variation}
          className="relative px-5 md:px-16"
        >
          {/* Background Image Layer */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Base light background */}
            <div className="absolute inset-0 bg-white" />

            {/* Background image from Prismic */}
            {isFilled.image(background) && (
              <div className="absolute inset-0 overflow-hidden">
                <PrismicNextImage
                  field={background}
                  className="absolute w-full h-full left-0 top-0 object-cover max-w-none"
                  fill={false}
                  priority
                />
              </div>
            )}
          </div>

          <div className="relative pt-[100px] pb-[80px] max-w-screen-2xl mx-auto flex flex-col lg:flex-row gap-10 items-center">
            {/* Left Content - Title & Description */}
            <div
              ref={contentRef}
              className="flex-1 flex flex-col gap-10 text-[#041018] opacity-0 translate-y-8 transition-all duration-700 ease-out lg:ml-[12vw]"
            >
              {isFilled.richText(title) && (
                <PrismicRichText
                  field={title}
                  components={{
                    heading2: ({ children }) => (
                      <h2 className="text-[32px] md:text-[52px] font-extrabold leading-[1.2]">
                        {children}
                      </h2>
                    ),
                  }}
                />
              )}

              {isFilled.richText(description) && (
                <div className="text-lg">
                  <PrismicRichText
                    field={description}
                    components={{
                      paragraph: ({ children }) => (
                        <p className="leading-[1.4]">{children}</p>
                      ),
                    }}
                  />
                </div>
              )}
            </div>

            {/* Right Content - Form Card */}
            <div
              id="lead-magnet-form"
              ref={formAreaRef}
              className="flex-1 flex justify-end opacity-0 translate-y-8 transition-all duration-700 ease-out delay-150 lg:mr-[10vw] scroll-mt-24"
            >
              <div className="w-full max-w-[446px] bg-[#041118] border-[5px] border-white rounded-2xl p-6 shadow-[0px_4.325px_64.871px_0px_rgba(0,0,0,0.08)]">
                {form_title && (
                  <p className="text-xl font-medium leading-[1.2] text-white mb-6">
                    {form_title}
                  </p>
                )}
                {isFormDescAndGreenButton &&
                  isFilled.richText(form_description) && (
                    <div className="text-base leading-[1.5] text-white/80 mb-6">
                      <PrismicRichText
                        field={form_description}
                        components={{
                          paragraph: ({ children }) => (
                            <p className="mb-3 last:mb-0">{children}</p>
                          ),
                        }}
                      />
                    </div>
                  )}
                <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                  {form_fields?.map((field, index) =>
                    renderFormField(field, index),
                  )}

                  {errorMessage && (
                    <div className="text-red-400 text-sm text-center">
                      {errorMessage}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={
                      isFormDescAndGreenButton
                        ? 'primary-button'
                        : 'w-full bg-[rgba(234,234,234,0.3)] border-t border-[#eaeaea] rounded-full px-6 py-3 font-bold text-white text-center hover:bg-white/40 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
                    }
                  >
                    {isSubmitting ? 'Submitting...' : button_text || 'Download'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  // Original variation: lead_magnet_with_side_image_form
  return (
    <>
      {/* Logo Header Section - Solid green background, no texture */}
      <section className="bg-[#002726]">
        <div
          ref={logoRef}
          className="py-[30px] px-4 md:px-[200px] flex justify-center opacity-0 translate-y-8 transition-all duration-700 ease-out"
        >
          {isFilled.image(logo) && (
            <Link href="/" aria-label="Devsu home page">
              <PrismicNextImage
                field={logo}
                className="h-7 md:h-[29px] w-auto object-contain"
              />
            </Link>
          )}
        </div>
      </section>

      {/* Main Content Section - Textured background */}
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="relative"
        style={{ backgroundColor: '#002726' }}
      >
        {/* Background Image Layer */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Base teal background */}
          <div className="absolute inset-0 bg-[#002726]" />

          {/* Background image from Prismic */}
          {isFilled.image(background) && (
            <div className="absolute inset-0 overflow-hidden">
              <PrismicNextImage
                field={background}
                className="absolute w-full h-[226%] left-0 top-[-12%] object-cover max-w-none"
                fill={false}
                priority
              />
            </div>
          )}

          {/* Overlay for depth effect */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              background:
                'radial-gradient(ellipse at center, rgba(0,100,90,0.5) 0%, transparent 70%)',
            }}
          />
        </div>

        {/* Main Content Area */}
        <div className="relative pt-[64px] pb-[100px] px-4 md:px-8 lg:px-[200px]">
          <div className="relative max-w-[1140px] mx-auto flex flex-col gap-10 items-center">
            {/* Title & Description */}
            <div
              ref={contentRef}
              className="flex flex-col gap-4 items-center text-center opacity-0 translate-y-8 transition-all duration-700 ease-out"
            >
              {isFilled.richText(title) && (
                <div className="max-w-[964px]">
                  <PrismicRichText
                    field={title}
                    components={{
                      heading2: ({ children }) => (
                        <h2 className="text-[32px] md:text-[52px] font-extrabold leading-[1.2] text-white">
                          {children}
                        </h2>
                      ),
                    }}
                  />
                </div>
              )}

              {isFilled.richText(description) && (
                <div className="max-w-[820px]">
                  <PrismicRichText
                    field={description}
                    components={{
                      paragraph: ({ children }) => (
                        <p className="text-lg md:text-xl font-medium leading-[1.4] text-white">
                          {children}
                        </p>
                      ),
                    }}
                  />
                </div>
              )}
            </div>

            {/* Image + Form Row */}
            <div
              id="lead-magnet-form"
              ref={formAreaRef}
              className="flex flex-col lg:flex-row gap-10 lg:gap-[60px] items-center justify-center py-[30px] opacity-0 translate-y-8 transition-all duration-700 ease-out scroll-mt-24"
            >
              {/* Resource Image */}
              {isFilled.image(resource_image) && (
                <div className="relative w-full max-w-[422px] lg:w-[422px] lg:h-[488px] shrink-0">
                  <PrismicNextImage
                    field={resource_image}
                    className="w-full h-auto object-contain"
                  />
                </div>
              )}

              {/* Form Card */}
              <div className="w-full max-w-[482px] lg:w-[482px] shrink-0 bg-[rgba(31,34,40,0.8)] backdrop-blur-[47.5px] rounded-2xl p-6 shadow-[0px_4.325px_64.871px_0px_rgba(0,0,0,0.08)] opacity-90">
                <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                  {form_fields?.map((field, index) =>
                    renderFormField(field, index),
                  )}

                  {errorMessage && (
                    <div className="text-red-400 text-sm text-center">
                      {errorMessage}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[rgba(234,234,234,0.3)] border-t border-[#eaeaea] rounded-full px-6 py-3 font-bold text-white text-center hover:bg-white/40 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : button_text || 'Download'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default LeadMagnetWithForm;
